import * as React from 'react';
import { Radio } from '@common/components/radio';
import { IndividualsTableProps } from "./individuals-table.types";
import { NewTableColumn, NewTableColumnAction, NewTableColumnPredefinedActionType, NewTableHeaderAction, NewTableHeaderPredefinedActionType } from '@common/components/new-table';
import { IndividualListDto } from '@common/dto/individual/list-dto';
import { I18n } from 'react-redux-i18n';
import ActionsButton, { TRIGGER_TYPE } from '@common/components/ActionsButton';
import { useAdminFunctionsController } from '@modules/pedigrees/controller/admin-functions';
import { AdminSmartListFunctionType } from '@common/dto/admin-function/type';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { BodyNameCell } from '@common/components/body-name-cell';
import { getCurrentUrl } from '@common/utils/url';
import { QuestionnaireItemMapDto } from '@common/dto/questionnaire/questionnaire-item-map-dto';
import { getIndividualEmail, SendInviteParams } from '@common/actions/individuals';
import { getTestAttributes } from '@common/utils/tests';
import { IndividualListSex } from '@common/dto/individual/individual-list-sex';
import { useTableController } from '@common/hooks/table';

export const useController = (props: IndividualsTableProps) => {
  const { 
    showInfoMessage,
    getQuestionnairesForNewQuestionnaire,
    onLaunchNewQuestionnaire,
    onOpenInviteQuestionnaire,
    adminFunctions,
    filters,
    columns,
    selectedItems,
    onIndividualRename,
    getData: getDataBase,
    getColumnId,
    openDatasheet,
    openPedigree,
    openTestResults,
    generateLetter,
    sendInvite,
    createBatchLetters,
  } = props;

  const {
    setChangingColumns,
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    handleSetCurrentSelectedRow,
    handleColumnsResizing,
    handleColumnsOrderChange,
  } = useTableController(props, { withFirstActionsColumn: true });

  const {
    handleRestoreColumnsToDefault,
    handleSetDefaultColumnsForAllUsers,
    handleApplyCurrentColumnsToAllUsers,
    handleSetDefaultColumnsForSpecificUsers,
    handleApplyCurrentColumnsToSpecificUsers,
    getProfiles,
    handleProfileSelectorSubmit,
    closeProfileSelector,
    profileSelectorOpened,
  } = useAdminFunctionsController(props, { columns, setChangingColumns });

  const [individualRenamingProps, setIndividualsRenamingProps] = React.useState<{ id: IndividualListDto['id']; name: string; } | undefined>();
  const [hasEmail, setHasEmail] = React.useState<boolean | undefined>(true)
  const [questionnaire, setQuestionnaire] = React.useState<QuestionnaireListDto | undefined>(undefined)

  const handleRenameIndividualClick = React.useCallback((_: string, individual: IndividualListDto) => {
    setIndividualsRenamingProps({
      name: individual.name,
      id: individual.id,
    });
  }, []);

  const closeIndividualRename = React.useCallback(() => {
    setIndividualsRenamingProps(undefined);
  }, []);

  const handleIndividualRename = React.useCallback(async (name: string) => {
    if (individualRenamingProps) {
      await onIndividualRename(individualRenamingProps.id, name);
      closeIndividualRename();
    }
  }, [onIndividualRename, closeIndividualRename, individualRenamingProps?.id]);

  const handleBatchLetters = React.useCallback(() => {
    createBatchLetters(selectedItems.map((x) => x.id));    
  }, [selectedItems, createBatchLetters]);

  
  const [newQuestionnaireProps, setNewQuestionnaireProps] = React.useState<{ individualName: string; individualId: IndividualListDto['id']; familyId: IndividualListDto['familyId'] } | undefined>();

  const handleNewQuestionnaireOpen = React.useCallback((id: IndividualListDto['id'], item: IndividualListDto) => {
    setNewQuestionnaireProps({
      individualId: item.id,
      individualName: item.name,
      familyId: item.familyId,
    });
  }, []);

  const closeNewQuestionnaire = React.useCallback(() => {
    setHasEmail(true);
    setQuestionnaire(undefined);
    setNewQuestionnaireProps(undefined);
  }, []);

  const handleNewQuestionnaire = React.useCallback(async (questionnaire: QuestionnaireListDto | undefined) => {
    if (!newQuestionnaireProps) {
      return;
    }

    const result = await getIndividualEmail({ fhq4: questionnaire?.fhq4, individualId: newQuestionnaireProps.individualId, questionnaireId: questionnaire?.questionnaireId })
    if (!result) {
      setHasEmail(false)
      setQuestionnaire(questionnaire)
    }
    else {
      await onLaunchNewQuestionnaire(newQuestionnaireProps.individualId, questionnaire);
      closeNewQuestionnaire();
    }
    
  }, [onLaunchNewQuestionnaire, newQuestionnaireProps, closeNewQuestionnaire]);

  const handleGetQuestionnairesForNewQuestionnaire = React.useCallback(async () => {
    if (!newQuestionnaireProps) {
      return [];
    }

    const res = await getQuestionnairesForNewQuestionnaire(newQuestionnaireProps.familyId);

    return res;
  }, [getQuestionnairesForNewQuestionnaire, newQuestionnaireProps?.familyId]);

  const [openQuestionnaireProps, setOpenQuestionnaireProps] = React.useState<{ individualName: string; individualId: IndividualListDto['id']; } | undefined>();

  const handleOpenQuestionnaireOpen = React.useCallback((id: IndividualListDto['id'], item: IndividualListDto) => {
    setOpenQuestionnaireProps({
      individualId: item.id,
      individualName: item.name,
    });
  }, []);

  const closeOpenQuestionnaire = React.useCallback(() => {
    setOpenQuestionnaireProps(undefined);
  }, []);

  const handleOpenQuestionnaire = React.useCallback(async (questionnaire: QuestionnaireItemMapDto) => {
    if (!openQuestionnaireProps) {
      return;
    }

    const currentUrl = getCurrentUrl();
    const { url } = await onOpenInviteQuestionnaire(questionnaire, currentUrl);

    window.location.href = url;
    closeOpenQuestionnaire();
  }, [onOpenInviteQuestionnaire, openQuestionnaireProps, closeOpenQuestionnaire]);

  const [newInviteProps, setNewInviteProps] = React.useState<{ individualName: string; individualId: IndividualListDto['id']; } | undefined>();

  const handleNewInviteOpen = React.useCallback((id: IndividualListDto['id'], item: IndividualListDto) => {
    setNewInviteProps({
      individualId: item.id,
      individualName: item.name,
    });
  }, []);

  const closeNewInvite = React.useCallback(() => {
    setNewInviteProps(undefined);
  }, []);

  const handleNewInviteSend = React.useCallback(async (data: SendInviteParams) => {
    if (!newInviteProps) {
      return;
    }

    await sendInvite(data);
    closeNewInvite();
    showInfoMessage(I18n.t('The Questionnaire invitation emails were successfully submitted'));
  }, [newInviteProps, closeNewInvite, sendInvite]);


  const getSexOptions = React.useCallback(
    (individual: IndividualListDto) => {

      const list: Record<IndividualListSex, string> = {
        [IndividualListSex.male]: "Male",
        [IndividualListSex.female]: "Female",
        [IndividualListSex.unknown]: "Unknown"
      };

      return Object.keys(list).map((key) => ({
        value: key,
        label: list[key as IndividualListSex],
        onClick: () =>
          props.changeSex({
            newGender: key as IndividualListSex,
            globalId: individual.id
          }),
      }));
    },
    [props.changeSex]
  );

  const getActions = (original: IndividualListDto) => {
    if (selectedItems.length > 1) {
      return [{ label: 'Batch Letters', onClick: handleBatchLetters }].map((x) => ({ ...x, label: I18n.t(x.label) }));
    }

    return [
      { label: "Rename Individual", onClick: handleRenameIndividualClick },
      { label: "Open Individual Datasheet", onClick: openDatasheet },
      { label: "Open Pedigree", onClick: openPedigree },
      { label: "Open Test Results", onClick: openTestResults },
      { label: "New Invite", onClick: handleNewInviteOpen },
      {
        label: "Launch New Questionnaire",
        onClick: handleNewQuestionnaireOpen,
      },
      {
        label: "Open Questionnaire",
        onClick: handleOpenQuestionnaireOpen,
        condition: original.invitesCount
          ? Number(original.invitesCount) > 0
          : false,
      },
      { label: "Generate Letter", onClick: generateLetter },
      {
        label: (
          <ActionsButton
            className="PedigreesIndividualsTable__update-status__btn"
            withBackdrop={false}
            classes={{
              menuContainer: "PedigreesIndividualsTable__update-status__menu",
            }}
            id={
              getTestAttributes({
                id: "ped-home-tab-bpane-samp-tab-list-act-menubar-item9",
              }).id
            }
            triggerType={TRIGGER_TYPE.hover}
            trigger={
              <React.Fragment>
                {I18n.t("Change Sex")}{" "}
                <span style={{ marginLeft: 4 }} className="fa fa-angle-right" />
              </React.Fragment>
            }
          >
            {getSexOptions(original).map((x) => (
              <div
                key={x.label}
                className="PedigreesIndividualsTable__update-status__menu__item"
                onClick={x.onClick}
              >
                <Radio
                  checked={x.value === original.genderIdentity}
                  onChange={x.onClick}
                >
                  <span style={{ marginLeft: 4 }}>
                    {I18n.t(x.label)}
                  </span>
                </Radio>
              </div>
            ))}
          </ActionsButton>
        ),
      },
    ]
      .filter((x) => x.condition ?? true)
      .map((x) => ({
        ...x,
        label: typeof x.label === "string" ? I18n.t(x.label) : x.label,
      }));
  };

  const renderActionsButton = React.useCallback(({ row: { original } }: { row: { original: IndividualListDto } }) => {
    return (
      <ActionsButton
        id={original.id} 
        stopPropagationOnRoot
        data={original} 
        menuItems={getActions(original)}
        setCurrent={handleSetCurrentSelectedRow}
      />
    );
  }, [getActions, handleSetCurrentSelectedRow]);

  const renderIndividualNameBodyCell = React.useCallback(({ getValue, row: { original } }: { getValue: () => unknown; row: { original: IndividualListDto } }) => {
    return (
      <BodyNameCell
        item={original}
        onClick={openDatasheet as (itemId: string | number) => void}
        label={getValue() as string}
      />
    );
  }, [openDatasheet]);

  // TODO check all ids

  const tableColumns = React.useMemo((): Array<NewTableColumn<IndividualListDto>> => {
    const defaultWidth = 100;
    const upnCellId = 17;

    return [
      { 
        disableActions: true,
        enableResizing: false,
        enableSorting: false,
        header: I18n.t('Actions'), 
        accessorKey: 'ACTIONS_COLUMNS_LOCAL',
        id: 'ACTIONS_COLUMNS_LOCAL',
        cell: renderActionsButton,
        minSize: 65,
        size: 65,
        className: 'table-actions-cell'
      },
      ...columns.map((x) => ({
        id: getColumnId(x),
        accessorKey: x.field.id.toString(),
        enableDragAndDrop: true,
        enableSorting: true,
        minSize: 20,
        size: x.width || defaultWidth,
        cell: x.field.id === upnCellId ? renderIndividualNameBodyCell : undefined,
        header: x.field.name,
        filter: { 
          type: x.field.type,
          options: x.field.options,
          outputDateFormat: 'YYYY-MM-DD'
        }
      }))
    ];
  }, [columns, getColumnId, renderActionsButton, renderIndividualNameBodyCell]);

  type TableAction<T> = T & {
    condition?: boolean; 
    adminFunction?: AdminSmartListFunctionType;
  };

  const getColumnActions = () => {
    const baseList: Array<TableAction<NewTableColumnAction>> = [
      { type: NewTableColumnPredefinedActionType.filters, condition: Boolean(filters) },
      { adminFunction: AdminSmartListFunctionType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), className: 'PedigreesIndividualsTable__column-action-top-divider', onClick: handleSetDefaultColumnsForAllUsers },
      { adminFunction: AdminSmartListFunctionType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: handleSetDefaultColumnsForSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: handleApplyCurrentColumnsToAllUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: handleApplyCurrentColumnsToSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: handleRestoreColumnsToDefault },
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableColumnPredefinedActionType.addColumn, className: 'PedigreesIndividualsTable__column-action-top-divider' },
      { adminFunction: AdminSmartListFunctionType.deleteColumn, type: NewTableColumnPredefinedActionType.deleteColumn },
      { type: NewTableColumnPredefinedActionType.clearSorting },
    ];

    return baseList.map((x) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : false;
      }

      return x;
    })
    .filter((x) => x.condition ?? true);
  };

  const getHeaderActions = () => {
    const baseList: Array<TableAction<NewTableHeaderAction>> = [
      { adminFunction: AdminSmartListFunctionType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), onClick: handleSetDefaultColumnsForAllUsers },
      { adminFunction: AdminSmartListFunctionType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: handleSetDefaultColumnsForSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: handleApplyCurrentColumnsToAllUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: handleApplyCurrentColumnsToSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: handleRestoreColumnsToDefault },
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableHeaderPredefinedActionType.addColumn, className: 'PedigreesIndividualsTable__column-action-top-divider' },
    ]
    
    return baseList.map((x) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : false;
      }

      return x;
    })
    .filter((x) => x.condition ?? true);;
  };

  const headerActions = getHeaderActions();
  const columnActions = getColumnActions();

  const getHeaderElementId = React.useCallback(() => {
    return 'ped-home-tab-bpane-samp-tab-list-grid-thead-row0';
  }, []);

  const getHeaderCellElementId = React.useCallback((column: NewTableColumn<IndividualListDto>, columnIndex: number) => {
    return `ped-home-tab-bpane-samp-tab-list-grid-thead-row0-cell${columnIndex}`;
  }, []);

  const getBodyRowElementId = React.useCallback((dataItem: IndividualListDto, rowIndex: number) => {
    return `ped-home-tab-bpane-samp-tab-list-grid-tbody-row${rowIndex}`;
  }, []);

  const getBodyCellElementId = React.useCallback((column: NewTableColumn<IndividualListDto>, columnIndex: number, dataItem: IndividualListDto, rowIndex: number) => {
    return `ped-home-tab-bpane-samp-tab-list-grid-tbody-row${rowIndex}-cell${columnIndex}`;
  }, []);

  return {
    newInviteProps,
    closeNewInvite,
    handleNewInviteSend,
    closeOpenQuestionnaire,
    handleOpenQuestionnaire,
    openQuestionnaireProps,
    handleGetQuestionnairesForNewQuestionnaire,
    handleNewQuestionnaire,
    closeNewQuestionnaire,
    newQuestionnaireProps,
    handleIndividualRename,
    individualRenamingProps,
    closeIndividualRename,
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    getProfiles,
    handleProfileSelectorSubmit,
    closeProfileSelector,
    profileSelectorOpened,
    tableColumns,
    headerActions,
    columnActions,
    getData: getDataBase,
    handleColumnsResizing,
    handleColumnsOrderChange,
    getHeaderElementId,
    getHeaderCellElementId,
    getBodyRowElementId,
    getBodyCellElementId,
    hasEmail,
    questionnaire,
  };
};